import React, { useState } from 'react';
import { Check, Loader2, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { feedbackQuestions } from '../../../config/feedbackQuestions';
import { FeedbackAnswer } from '../../../hooks/useResultSender';
import { SendError, feedbackErrorMessageKey } from '../../../errors/sendError';
import { ScaleQuestion } from './questions/ScaleQuestion';
import { ChipsQuestion } from './questions/ChipsQuestion';
import { TaxonomyQuestion, TaxonomyValue } from './questions/TaxonomyQuestion';

export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface FeedbackFormProps {
  status: FormStatus;
  /** Classified send error, when status === 'error'. Translated via its `kind`. */
  error?: SendError | null;
  onSubmit: (answers: FeedbackAnswer[]) => void;
  onSkip: () => void;
  onRetry?: () => void;
}

type ScaleAnswers = Record<string, number | null>;
/** Refinement factor per scale question (only for questions declaring `refine`). */
type ScaleFactors = Record<string, string | null>;
type ChipsAnswers = Record<string, Record<string, string | null>>;
type TaxonomyAnswers = Record<string, TaxonomyValue | null>;

/**
 * Strip empty answers before submission. The backend stores whatever it
 * receives, so we keep the payload clean: an unanswered scale becomes nothing,
 * not `{ value: null }`.
 */
function buildAnswers(
  scales: ScaleAnswers,
  factors: ScaleFactors,
  chips: ChipsAnswers,
  taxonomies: TaxonomyAnswers
): FeedbackAnswer[] {
  const answers: FeedbackAnswer[] = [];

  for (const q of feedbackQuestions) {
    if (q.type === 'scale') {
      const v = scales[q.id];
      if (v !== null && v !== undefined) {
        // A scale with a refinement always sends an object, so consumers get a
        // stable shape; `factor` is omitted when the referee didn't pick one.
        const factor = factors[q.id];
        if (q.refine) {
          answers.push({
            questionId: q.id,
            value: { value: v, ...(factor ? { factor } : {}) },
          });
        } else {
          answers.push({ questionId: q.id, value: v });
        }
      }
    } else if (q.type === 'chips') {
      const groupValues = chips[q.id] ?? {};
      const filtered = Object.fromEntries(
        Object.entries(groupValues).filter(([, v]) => v !== null && v !== undefined)
      );
      if (Object.keys(filtered).length > 0) {
        answers.push({ questionId: q.id, value: filtered });
      }
    } else if (q.type === 'taxonomy') {
      const v = taxonomies[q.id];
      if (!v) continue;
      const hasTag = Boolean(v.category && v.subItem);
      const hasNote = Boolean(v.note && v.note.trim());
      if (hasTag || hasNote) {
        answers.push({
          questionId: q.id,
          value: {
            ...(hasTag ? { category: v.category, subItem: v.subItem } : {}),
            ...(hasNote ? { note: v.note.trim() } : {}),
          },
        });
      }
    }
  }

  return answers;
}

export function FeedbackForm({
  status,
  error,
  onSubmit,
  onSkip,
  onRetry,
}: FeedbackFormProps) {
  const { t } = useTranslation();

  const [scales, setScales] = useState<ScaleAnswers>({});
  const [factors, setFactors] = useState<ScaleFactors>({});
  const [chips, setChips] = useState<ChipsAnswers>({});
  const [taxonomies, setTaxonomies] = useState<TaxonomyAnswers>({});
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleSubmit = () => {
    const answers = buildAnswers(scales, factors, chips, taxonomies);
    onSubmit(answers);
  };

  const isBusy = status === 'submitting';
  const isSuccess = status === 'success';
  const isError = status === 'error';

  let submitLabel = t('screens.feedback.submit');
  if (isBusy) submitLabel = t('screens.feedback.submitting');
  if (isSuccess) submitLabel = t('screens.feedback.submitted');

  const submitClasses = isSuccess
    ? 'bg-emerald-600 text-white'
    : 'bg-gray-800 text-white';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-4 px-2"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
    >
      <div className="w-[90%] max-w-md max-h-[90vh] bg-stone-100 rounded-xl shadow-lg flex flex-col overflow-hidden">
        <header className="sticky top-0 z-10 bg-stone-100 px-4 pt-4 pb-3 grid grid-cols-[1fr_auto] items-center gap-3 border-b border-gray-200">
          <div className="text-left">
            <h1 id="feedback-title" className="text-xl font-bold text-gray-800 mb-0.5">
              {t('screens.feedback.title')}
            </h1>
            <p className="text-[13px] text-gray-500">{t('screens.feedback.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onSkip}
            disabled={isBusy}
            className="bg-transparent border border-gray-200 text-gray-500 hover:bg-gray-200 hover:text-gray-800 font-semibold text-sm py-2 px-3.5 rounded-lg active:scale-95 disabled:opacity-50"
          >
            {t('screens.feedback.skip')}
          </button>
        </header>

        <div className="overflow-y-auto px-4 pt-4 pb-6 flex flex-col">

        {feedbackQuestions.map((q, idx) => {
          const questionNumber = idx + 1;
          if (q.type === 'scale') {
            return (
              <ScaleQuestion
                key={q.id}
                questionNumber={questionNumber}
                labelKey={q.labelKey}
                variant={q.variant}
                options={q.options}
                value={scales[q.id] ?? null}
                onChange={(v) => setScales((prev) => ({ ...prev, [q.id]: v }))}
                refine={q.refine}
                factor={factors[q.id] ?? null}
                onFactorChange={(f) => setFactors((prev) => ({ ...prev, [q.id]: f }))}
              />
            );
          }
          if (q.type === 'chips') {
            return (
              <ChipsQuestion
                key={q.id}
                questionNumber={questionNumber}
                labelKey={q.labelKey}
                groups={q.groups}
                value={chips[q.id] ?? {}}
                onChange={(groupId, v) =>
                  setChips((prev) => ({
                    ...prev,
                    [q.id]: { ...(prev[q.id] ?? {}), [groupId]: v },
                  }))
                }
              />
            );
          }
          if (q.type === 'taxonomy') {
            return (
              <TaxonomyQuestion
                key={q.id}
                questionNumber={questionNumber}
                labelKey={q.labelKey}
                hintKey={q.hintKey}
                notePlaceholderKey={q.notePlaceholderKey}
                categories={q.categories}
                value={taxonomies[q.id] ?? null}
                onChange={(v) => setTaxonomies((prev) => ({ ...prev, [q.id]: v }))}
              />
            );
          }
          return null;
        })}

        <div className="mt-1 mb-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isBusy || isSuccess}
            className={`w-full py-3.5 rounded-[10px] font-semibold text-[15px] transition-all active:scale-[0.98] disabled:cursor-not-allowed flex items-center justify-center gap-2 ${submitClasses}`}
          >
            {isBusy && <Loader2 size={18} className="animate-spin" />}
            {isSuccess && <Check size={18} strokeWidth={2.5} />}
            {submitLabel}
          </button>
          {isError && (
            <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
              <div className="flex items-start justify-between gap-3">
                <span>{error ? t(feedbackErrorMessageKey(error.kind)) : t('screens.feedback.error')}</span>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="font-semibold underline shrink-0"
                  >
                    {t('screens.feedback.retry')}
                  </button>
                )}
              </div>

              {error && (
                <>
                  <button
                    type="button"
                    onClick={() => setDetailsOpen((open) => !open)}
                    className="mt-2 inline-flex items-center gap-1 text-red-600 text-xs hover:underline"
                    aria-expanded={detailsOpen}
                  >
                    <ChevronRight
                      size={14}
                      className={`transition-transform ${detailsOpen ? 'rotate-90' : ''}`}
                    />
                    {t('errors.send.technicalDetails')}
                  </button>
                  {detailsOpen && (
                    <pre className="mt-2 p-2 bg-red-100 text-red-700 text-xs rounded whitespace-pre-wrap break-words">
                      {error.httpStatus ? `HTTP ${error.httpStatus}\n` : ''}
                      {error.technical}
                    </pre>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
